import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getSuitEmoji } from '../utils/cardHelpers';

/**
 * CardSelector component renders two dropdown menus for selecting
 * Tarot cards with orientation selection.
 */
export default function CardSelector({ cards = [], loading = false, onSelectCard1, onSelectCard2, onSwitchToVirtualDeck }) {
  // Track selected cards and their orientations
  const [selectedCard1, setSelectedCard1] = useState(null);
  const [selectedCard2, setSelectedCard2] = useState(null);
  const [orientation1, setOrientation1] = useState('upright');
  const [orientation2, setOrientation2] = useState('upright');

  const handleCard1Change = (e) => {
    const cardId = e.target.value;
    const card = cardId ? cards.find(c => c.id === cardId) : null;
    setSelectedCard1(card);
    // Only notify parent when we have both card and orientation
    if (card) {
      onSelectCard1({ ...card, orientation: orientation1 });
    } else {
      onSelectCard1(null);
    }
  };

  const handleCard2Change = (e) => {
    const cardId = e.target.value;
    const card = cardId ? cards.find(c => c.id === cardId) : null;
    setSelectedCard2(card);
    // Only notify parent when we have both card and orientation
    if (card) {
      onSelectCard2({ ...card, orientation: orientation2 });
    } else {
      onSelectCard2(null);
    }
  };

  const handleOrientation1Change = (e) => {
    const newOrientation = e.target.value;
    setOrientation1(newOrientation);
    // Update parent with new orientation
    if (selectedCard1) {
      onSelectCard1({ ...selectedCard1, orientation: newOrientation });
    }
  };

  const handleOrientation2Change = (e) => {
    const newOrientation = e.target.value;
    setOrientation2(newOrientation);
    // Update parent with new orientation
    if (selectedCard2) {
      onSelectCard2({ ...selectedCard2, orientation: newOrientation });
    }
  };

  return (
    <div className="space-y-6">
      {/* Virtual Deck Button */}
      {onSwitchToVirtualDeck && (
        <div className="text-center">
          <button
            onClick={onSwitchToVirtualDeck}
            disabled={loading || cards.length < 2}
            className="btn-mystical text-sm px-6 py-2"
          >
            ✨ Select from Virtual Deck
          </button>
          <p className="text-xs text-amber-400 mt-2 opacity-70">
            Pick cards from the interactive spread
          </p>
        </div>
      )}

      {/* Main Card Selection */}
      <div className="flex flex-col lg:flex-row gap-6 justify-center">
        <div className="flex-1 space-y-4">
          <label className="heading-tertiary block mb-3">Choose Your First Path</label>
          <select
            className="input-mystical"
            value={selectedCard1?.id || ""}
            onChange={handleCard1Change}
            disabled={loading}
          >
            <option value="">
              {loading ? 'Consulting the Cards...' : 'Select your first card...'}
            </option>
            {cards.map((card) => {
              const emoji = card.arcana === 'Minor' && card.suit ? getSuitEmoji(card.suit) : '';
              return (
                <option key={card.id} value={card.id}>
                  {emoji && `${emoji} `}{card.name}
                </option>
              );
            })}
          </select>
          
          {/* Orientation selection for card 1 */}
          {selectedCard1 && (
            <div className="orientation-selector">
              <label className="heading-quaternary block mb-2 text-center">
                ✦ Card Orientation ✦
              </label>
              <div className="flex gap-3 justify-center flex-wrap">
                <label className="orientation-option">
                  <input
                    type="radio"
                    name="orientation1"
                    value="upright"
                    checked={orientation1 === 'upright'}
                    onChange={handleOrientation1Change}
                    className="mr-2"
                  />
                  <span>⬆ Upright</span>
                </label>
                <label className="orientation-option">
                  <input
                    type="radio"
                    name="orientation1"
                    value="reversed"
                    checked={orientation1 === 'reversed'}
                    onChange={handleOrientation1Change}
                    className="mr-2"
                  />
                  <span>⬇ Reversed</span>
                </label>
              </div>
              {/* Display description for selected card */}
              {selectedCard1 && (
                <div className="card-meaning-display">
                  <p className="text-sm italic text-amber-300 font-semibold">
                    {selectedCard1.description}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex-1 space-y-4">
          <label className="heading-tertiary block mb-3">Choose Your Second Path</label>
          <select
            className="input-mystical"
            value={selectedCard2?.id || ""}
            onChange={handleCard2Change}
            disabled={loading}
          >
            <option value="">
              {loading ? 'Consulting the Cards...' : 'Select your second card...'}
            </option>
            {cards.map((card) => {
              const emoji = card.arcana === 'Minor' && card.suit ? getSuitEmoji(card.suit) : '';
              return (
                <option key={card.id} value={card.id}>
                  {emoji && `${emoji} `}{card.name}
                </option>
              );
            })}
          </select>
          
          {/* Orientation selection for card 2 */}
          {selectedCard2 && (
            <div className="orientation-selector">
              <label className="heading-quaternary block mb-2 text-center">
                ✦ Card Orientation ✦
              </label>
              <div className="flex gap-3 justify-center flex-wrap">
                <label className="orientation-option">
                  <input
                    type="radio"
                    name="orientation2"
                    value="upright"
                    checked={orientation2 === 'upright'}
                    onChange={handleOrientation2Change}
                    className="mr-2"
                  />
                  <span>⬆ Upright</span>
                </label>
                <label className="orientation-option">
                  <input
                    type="radio"
                    name="orientation2"
                    value="reversed"
                    checked={orientation2 === 'reversed'}
                    onChange={handleOrientation2Change}
                    className="mr-2"
                  />
                  <span>⬇ Reversed</span>
                </label>
              </div>
              {/* Display description for selected card */}
              {selectedCard2 && (
                <div className="card-meaning-display">
                  <p className="text-sm italic text-amber-300 font-semibold">
                    {selectedCard2.description}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
            </div>
    </div>
  );
}

CardSelector.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    suit: PropTypes.string,
    description: PropTypes.string,
  })),
  loading: PropTypes.bool,
  onSelectCard1: PropTypes.func.isRequired,
  onSelectCard2: PropTypes.func.isRequired,
  onSwitchToVirtualDeck: PropTypes.func,
};

CardSelector.defaultProps = {
  cards: [],
  loading: false,
  onSwitchToVirtualDeck: null,
};
